/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Stack,
    Flex,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Discount from './discount'
import {HStack} from '@chakra-ui/layout'
import CurrentPrice from './current-price'
import ListPrice from './list-price'
import {remCalc} from '../../theme/_utils'

/**
 * @param priceData - price info extracted from a product
 * // If a product is a set,
 *      on PLP, Show From X where X is the lowest of its children
 *      on PDP, Show From X where X is the lowest of its children and
 *          the set children will have it own price as From X (cross) Y
 * // if a product is a master
 *      on PLP and PDP, show From X (cross) Y , the X and Y are
 *          current and list price of variant that has the lowest price (including promotionalPrice)
 * // if a standard/bundle
 *      show exact price on PLP and PDP as X (cross) Y
 * @param currency - currency
 * @param quantity - quantity to take into the account for price display
 * @param currentPriceProps - extra props to be passing to CurrentPrice component
 * @param listPriceProps - extra props to be passing to ListPrice component
 * @param labelForA11y - label to be used for a11y
 * @param productDetailPriceView - boolean to be used for showing the correct omnibus view from PDP and PLP
 */
const DisplayPrice = ({
    labelForA11y,
    priceData,
    currency,
    quantity = 1,
    priceContainerProps = {},
    currentPriceProps = {},
    listPriceProps = {},
    discountProps = {},
    modalPrice = false,
    productDetailPriceView,
    hideListingPrice = false,
    showSuggestesLabel = false,
    variant = 'default',
    modalPriceRecommended = false,
    isMinicart = false,
    notShoppableCountry = false,
}) => {
    const styles = useMultiStyleConfig('Modal', {variant})
    const {listPrice, currentPrice, isASet, isMaster, isOnSale, isRange, showOmnibusPriceDiscount} =
        priceData
    const modalCurrentPriceProps = modalPrice
        ? {sx: styles.productPricing, ...currentPriceProps}
        : currentPriceProps

    const modalListPriceProps = modalPrice
        ? {
              sx: styles.omnibusPricing,
              ...listPriceProps,
          }
        : listPriceProps

    if (notShoppableCountry) {
        return <></>
    }
    const renderCurrentPrice = (isRange) => (
        <CurrentPrice
            labelForA11y={labelForA11y}
            price={currentPrice * quantity}
            as="b"
            currency={currency}
            isRange={isRange}
            variant={variant}
            {...modalCurrentPriceProps}
        />
    )
    const renderListPrice = (isRange, omnibusScenario) =>
        listPrice &&
        (omnibusScenario ? null : (
            <ListPrice
                labelForA11y={labelForA11y}
                currency={currency}
                price={listPrice * quantity}
                isRange={isRange}
                {...modalListPriceProps}
                showSuggestesLabel={showSuggestesLabel}
                variant={variant}
            />
        ))

    const renderDiscount = () =>
        listPrice && (
            <Discount
                price={(listPrice - currentPrice) / listPrice}
                variant={variant}
                {...discountProps}
            />
        )

    const renderPriceSet = (isRange, modalPrice) =>
        modalPrice ? (
            <Stack
                {...styles.pricingAlignment}
                flexDirection={modalPriceRecommended ? 'row' : 'column'}
            >
                <Flex {...styles.productPricing}>{renderCurrentPrice(isRange)} </Flex>
                {isOnSale && renderListPrice(isRange, showOmnibusPriceDiscount) && (
                    <Box {...styles.omnibusPricing} textAlign={isMinicart ? 'start' : 'end'}>
                        {renderListPrice(isRange, showOmnibusPriceDiscount)}
                    </Box>
                )}
            </Stack>
        ) : productDetailPriceView ? (
            <HStack gap={remCalc(10)} {...priceContainerProps}>
                {renderCurrentPrice(isRange)}{' '}
                {isOnSale && renderListPrice(isRange, hideListingPrice)}
                {isOnSale && renderDiscount()}
            </HStack>
        ) : (
            <Stack id="product-render-current">
                {renderCurrentPrice(isRange)}{' '}
                {isOnSale && renderListPrice(isRange, showOmnibusPriceDiscount)}
            </Stack>
        )

    if (isASet) {
        return renderCurrentPrice(true)
    }

    if (isMaster) {
        return renderPriceSet(isRange, modalPrice)
    }

    return <Box>{renderPriceSet(false, modalPrice)}</Box>
}

DisplayPrice.propTypes = {
    priceData: PropTypes.shape({
        currentPrice: PropTypes.number.isRequired,
        isOnSale: PropTypes.bool.isRequired,
        listPrice: PropTypes.number,
        pricePerUnit: PropTypes.number,
        isASet: PropTypes.bool,
        isMaster: PropTypes.bool,
        isRange: PropTypes.bool,
        maxPrice: PropTypes.number,
        tieredPrice: PropTypes.number,
        showOmnibusPriceDiscount: PropTypes.bool,
    }),
    currency: PropTypes.string.isRequired,
    labelForA11y: PropTypes.string,
    priceContainerProps: PropTypes.object,
    currentPriceProps: PropTypes.object,
    listPriceProps: PropTypes.object,
    discountProps: PropTypes.object,
    modalPrice: PropTypes.bool,
    quantity: PropTypes.number,
    productDetailPriceView: PropTypes.bool,
    hideListingPrice: PropTypes.bool,
    showSuggestesLabel: PropTypes.bool,
    variant: PropTypes.string,
    modalPriceRecommended: PropTypes.bool,
    isMinicart: PropTypes.bool,
    notShoppableCountry: PropTypes.bool,
}

export default DisplayPrice
